import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  debounce,
  useTheme,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { Building2Icon, ConstructionIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  type DevelopmentSelectFragment,
  Development_Status_Enum_Enum,
} from '@graphql/__generated__/graphql';
import {
  RaAutoComplete,
  type RaAutocompleteProps,
} from '@modules/_shared/components/data-grid/RaAutoComplete';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { getDevelopmentPropertyAddress } from '@modules/_shared/utils/propertyTemplateFunctions';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

export const DEVELOPMENT_SELECT_FRAGMENT = gql(/* GraphQL */ `
  fragment DevelopmentSelect on developments {
    id
    title
    status
    development_images(order_by: { order_nr: asc }, limit: 1) {
      image {
        id
        url
      }
    }
    lots {
      id
      property {
        id
        route
        street_number
        postcode
        locality
      }
    }
  }
`);

export const SEARCH_DEVELOPMENTS = gql(/* GraphQL */ `
  query SearchDevelopments($search: String!, $where: developments_bool_exp!) {
    developments(
      where: { _and: [{ title: { _ilike: $search } }, $where] }
      order_by: [{ title: asc }, { created_at: desc }]
      limit: 10
    ) {
      id
      ...DevelopmentSelect
    }
  }
`);

const GET_DEFAULT_DEVELOPMENT = gql(/* GraphQL */ `
  query GetDefaultDevelopment($id: uuid!) {
    developments_by_pk(id: $id) {
      id
      ...DevelopmentSelect
    }
  }
`);

type DevelopmentSelectProps = Pick<
  RaAutocompleteProps<DevelopmentSelectFragment, false>,
  'autoFocus' | 'disabled' | 'sx' | 'InputProps' | 'loading'
> & {
  developmentId?: string | null;
  onChange?: (developmentId: string | null) => void;
  shareableOnly?: boolean;
};

export const DevelopmentSelectItem: React.FC<{
  development: DevelopmentSelectFragment;
  props: React.HTMLAttributes<HTMLLIElement>;
}> = ({ development, props }) => {
  const { text, palette: colors } = useTheme();
  const { t } = useLocale();

  const address = useMemo(
    () => getDevelopmentPropertyAddress(development),
    [development],
  );

  return (
    <ListItem
      {...props}
      key={development.id}
      sx={{
        padding: '1px 10px',
      }}
    >
      <ListItemIcon>
        <ListItemAvatar sx={{ paddingRight: 1 }}>
          <Avatar variant="rounded" sx={{ width: 55, height: 55 }}>
            {development.development_images &&
            development.development_images.length > 0 ? (
              <Image
                src={development.development_images[0]?.image.url ?? ''}
                options={{ w: 256, f: 'jpg' }}
                objectFit="cover"
              />
            ) : (
              <Building2Icon
                style={{ margin: 8 }}
                size={32}
                color={customPalette.gray300}
              />
            )}
          </Avatar>
        </ListItemAvatar>
      </ListItemIcon>
      <ListItemText
        primary={
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, ...text.ellipsis }}>
              {development.title ?? t('Untitled development')}
            </div>
            <div
              style={{
                ...text.caption,
                ...text.ellipsis,
                color: colors.grey[600],
              }}
            >
              {address != null ? formatAddress(address, ', ') : '-'}
            </div>
          </div>
        }
        secondary={development.status}
      />
    </ListItem>
  );
};

export const DevelopmentSelect = ({
  onChange,
  developmentId,
  autoFocus,
  disabled,
  loading,
  sx,
  InputProps,
  shareableOnly = false,
}: DevelopmentSelectProps) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<DevelopmentSelectFragment[]>([]);
  const prevDevelopmentId = useRef<string | undefined | null>(developmentId);
  const [selectedValue, setSelectedValue] = useState<
    (DevelopmentSelectFragment & { isRestricted?: true }) | null
  >(null);

  const { data: defaultDevelopment, loading: loadingDefaultDevelopment } =
    useQuery(GET_DEFAULT_DEVELOPMENT, {
      skip: developmentId == null,
      variables: { id: developmentId ?? '' },
    });

  useEffect(() => {
    if (defaultDevelopment?.developments_by_pk != null) {
      setSelectedValue(defaultDevelopment.developments_by_pk);
    } else if (developmentId != null) {
      // We have a development but we don't have access to it (permission denied).
      setSelectedValue({
        id: developmentId,
        status: Development_Status_Enum_Enum.Draft,
        development_images: [],
        lots: [],
        isRestricted: true,
      });
    }
  }, [defaultDevelopment, developmentId, t]);

  if (prevDevelopmentId.current !== developmentId) {
    prevDevelopmentId.current = developmentId;

    if (developmentId == null) {
      setSelectedValue(null);
    }
  }

  const [searchDevelopments, { loading: searchLoading, error }] = useLazyQuery(
    SEARCH_DEVELOPMENTS,
    { notifyOnNetworkStatusChange: true },
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        // Don't search if a value is already selected
        if (selectedValue != null) {
          return;
        }

        searchDevelopments({
          variables: {
            search: `%${value}%`,
            where: shareableOnly
              ? {
                  status: { _neq: Development_Status_Enum_Enum.Draft },
                  slug: { _is_null: false, _neq: '' },
                }
              : {},
          },
        }).then(response => {
          setOpen(true);
          setOptions(response.data?.developments ?? []);
        });
      }, 275),
    [searchDevelopments, selectedValue, shareableOnly],
  );

  const getOptionLabel = useCallback(
    (option: DevelopmentSelectFragment & { isRestricted?: true }) => {
      if (option.isRestricted) {
        return t('Restricted development');
      }

      const address = getDevelopmentPropertyAddress(option);

      return (
        option.title ??
        (address != null
          ? formatAddress(address, ', ')
          : t('Untitled development'))
      );
    },
    [t],
  );

  const handleChange = useCallback(
    (
      _event: React.SyntheticEvent<Element, Event>,
      value: (DevelopmentSelectFragment & { isRestricted?: true }) | null,
    ) => {
      setSelectedValue(value);
      onChange?.(value?.id ?? null);
    },
    [onChange],
  );

  const renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: DevelopmentSelectFragment & { isRestricted?: true },
    ) => {
      return <DevelopmentSelectItem development={option} props={props} />;
    },
    [],
  );

  return (
    <RaAutoComplete<DevelopmentSelectFragment & { isRestricted?: true }>
      sx={sx}
      InputProps={{
        ...InputProps,
        startAdornment: InputProps?.startAdornment ?? (
          <SelectStartIcon icon={ConstructionIcon} />
        ),
        placeholder: InputProps?.placeholder ?? t('Search developments'),
      }}
      open={open}
      setOpen={setOpen}
      options={options}
      setOptions={setOptions}
      loading={loading || loadingDefaultDevelopment || searchLoading}
      value={selectedValue}
      error={error != null}
      autoFocus={autoFocus}
      debouncedSearch={debouncedSearch}
      onChange={handleChange}
      disabled={disabled || selectedValue?.isRestricted}
      getOptionLabel={getOptionLabel}
      tooltip={
        selectedValue?.isRestricted
          ? t("You don't have access to this development")
          : undefined
      }
      renderOption={renderOption}
    />
  );
};
